import axios from "axios";
import { ClientSteps, Statistic } from "../../Constants";

/**
 * Base URL for statistics API.
 * Handles requests related to system statistics.
 */
const BASE_URL = `/m-1/statistics`;

/**
 * Service for fetching statistical data from the API.
 */
export const StatisticService = {
    get,
    getByType,
    resolveTypeForUser,
    resolveQuestionnaireTypeForStatisticName,
}

/**
 * Fetches all available system statistics.
 *
 * @returns {Promise<import('axios').AxiosResponse>} Axios response promise containing an array of statistics.
 */
async function get() {
    const response = await axios.get(`${BASE_URL}`);

    return response.data;
}

/**
 * Fetches a specific type of system statistics.
 *
 * @param {string} type - The specific statistics type to retrieve (must match backend allowed types).
 * 
 * @returns {Promise<import('axios').AxiosResponse>} Axios response promise containing the selected statistic.
 */
async function getByType(type) {
    if (type === null) {
        return null;
    }

    const response = await axios.get(`${BASE_URL}/${type}`);

    return response.data;
}

/**
 * Resolves the appropriate statistics type for a given user.
 *
 * This function determines which type of statistics should be fetched 
 * or displayed based on the user's profile, role, or preferences.
 *
 * @param {Object} user - The user object used to determine the statistics type.
 * 
 * @returns {string} The resolved statistics type (e.g., 'self-esteem-statistics').
 */
function resolveTypeForUser(user) {
    const scopes = [
        {
            type: Statistic.AutoAnalysisStatistics,
            values: [
                ClientSteps.AutoAnalysisNotStarted.value,
                ClientSteps.AutoAnalysisInProgress.value,
                ClientSteps.AutoAnalysisCompleted.value,
            ],
        },
        {
            type: Statistic.SelfEsteemStatistics,
            values: [
                ClientSteps.SelfEsteemNotStarted.value,
                ClientSteps.SelfEsteemInProgress.value,
                ClientSteps.SelfEsteemCompleted.value,
            ]
        },
        {
            type: Statistic.YourDevelopmentGoalStatistics,
            values: [
                ClientSteps.OrderNotStarted.value,
                ClientSteps.OrderCompleted.value,
            ],
        },
        {
            type: Statistic.YourSecondDevelopmentGoalStatistics,
            values: [
                ClientSteps.OrderSecondNotStarted.value,
                ClientSteps.OrderSecondCompleted.value,
            ],
        },
        {
            type: Statistic.ClientAgreementStatistics,
            values: [
                ClientSteps.ContractNotSigned.value,
                ClientSteps.ContractSigned.value,
            ],
        },
        {
            type: Statistic.ClientSecondAgreementStatistics,
            values: [
                ClientSteps.ContractSecondNotSigned.value,
                ClientSteps.ContractSecondSigned.value,
            ],
        },
    ];

    for (const scope of scopes) {
        if (scope.values.includes(user.clientStep)) {
            return scope.type;
        }
    }

    return null;
}

/**
 * Maps a statistic name to its corresponding questionnaire type identifier.
 *
 * @param {string} name - The name of the statistic (from the Statistic enum).
 *
 * @returns {number | null} The corresponding questionnaire type ID, or null if not found.
 */
function resolveQuestionnaireTypeForStatisticName(name) {
    const typeMap = new Map([
        [Statistic.AutoAnalysisStatistics, 1],
        [Statistic.SelfEsteemStatistics, 2],
        [Statistic.YourDevelopmentGoalStatistics, 3],
        [Statistic.YourSecondDevelopmentGoalStatistics, 4],
    ]);

    return typeMap.get(name) ?? null;
}
